import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowBack as PrevIcon,
  ArrowForward as NextIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Markdown, InlineMarkdown, HeadingAnchor } from './Markdown';
import { parseGuide, searchGuide, slugify, termPattern, queryTerms, GuideSection } from '../utils/docSections';
import { useIsMobile } from '../hooks/useIsMobile';

/** `#calendar/moving-a-post` → ['calendar', 'moving-a-post'] */
const readHash = (hash: string): [string, string | undefined] => {
  const [section, sub] = decodeURIComponent(hash.replace(/^#/, '')).split('/');
  return [section || '', sub || undefined];
};

const hashFor = (section?: string, sub?: string) =>
  section ? `#${section}${sub ? `/${sub}` : ''}` : '';

/** The DOM id a subsection heading carries, so it can be scrolled to. */
const subId = (section: string, sub: string) => `${section}--${sub}`;

/** Wrap each typed term in a <mark>, so a reader sees why a result matched. */
const highlight = (text: string, query: string): React.ReactNode => {
  const terms = queryTerms(query);
  if (!terms.length) return text;
  // The same word-start rule the search uses, so only what matched is marked
  return text.split(termPattern(terms, 'gi')).map((part, i) =>
    i % 2 === 1 ? <Box key={i} component="mark" sx={{ bgcolor: 'warning.light', px: 0.25, borderRadius: 0.5 }}>{part}</Box> : part
  );
};

interface Props {
  /** The whole markdown document. */
  source: string;
  /** Where the page lives, for the links the heading anchors copy — `/guide`. */
  basePath: string;
  /** How the document is referred to in the search box and its messages — "the guide". */
  noun: string;
  /** Lines to drop from the intro on the overview, typically a tagline the page header repeats. */
  introOmit?: RegExp[];
}

/**
 * A long markdown document shown one section at a time, with contents, links and search.
 *
 * Shared by the User Guide and the API docs. Rendered whole, each was hundreds of lines on one
 * screen with no way to find anything. The document is split at its `## ` headings when read
 * (see utils/docSections) — the markdown never has to know it is paged — and every section and
 * subsection gets a URL: `#calendar`, or `#calendar/moving-a-post-to-another-day`. The hash is
 * the only state, so links can be shared, bookmarks work, and Back goes to the previous section.
 */
export const PagedDoc: React.FC<Props> = ({ source, basePath, noun, introOmit = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const doc = useMemo(() => parseGuide(source), [source]);
  const [sectionSlug, subSlug] = readHash(location.hash);
  const index = doc.sections.findIndex(s => s.slug === sectionSlug);
  // An unknown or missing hash shows the overview rather than an error: a link to a section
  // that has since been renamed should still land somewhere useful.
  const current: GuideSection | undefined = doc.sections[index];
  const searching = query.trim().length > 0;
  const results = useMemo(() => searchGuide(doc, query), [doc, query]);

  // A search starts at the top of its results, not wherever the last section was scrolled to
  useEffect(() => {
    if (searching) window.scrollTo({ top: 0 });
  }, [searching]);

  const go = (section?: string, sub?: string) => {
    setQuery('');
    navigate({ hash: hashFor(section, sub) });
  };

  // Land at the right place whenever the address changes: the subsection's heading when one
  // is named, otherwise the top of the page. After paint, so the section is on screen first.
  useEffect(() => {
    if (searching) return;
    const frame = window.requestAnimationFrame(() => {
      const target = current && subSlug ? document.getElementById(subId(current.slug, subSlug)) : null;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, searching, current, subSlug]);

  // Headings in the section being shown get ids and a "#" link: the section title links to
  // the section, each ### to its subsection.
  const anchorFor = (level: number, text: string): HeadingAnchor | undefined => {
    if (!current) return undefined;
    if (level === 2) return { id: current.slug, hash: hashFor(current.slug) };
    if (level === 3) {
      const sub = current.subsections.find(s => s.title === text) ||
        current.subsections.find(s => s.slug === slugify(text));
      if (sub) return { id: subId(current.slug, sub.slug), hash: hashFor(current.slug, sub.slug) };
    }
    return undefined;
  };

  const onAnchorClick = (anchor: HeadingAnchor) => {
    navigate({ hash: anchor.hash });
    // A courtesy: the address bar already holds the link if the clipboard is unavailable
    try {
      navigator.clipboard?.writeText(`${window.location.origin}${basePath}${anchor.hash}`)
        .then(() => setCopied(true))
        .catch(() => undefined);
    } catch {
      /* clipboard blocked — nothing to do */
    }
  };

  const searchBox = (
    <TextField
      size="small"
      fullWidth
      placeholder={`Search ${noun}`}
      value={query}
      onChange={e => setQuery(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );

  // Every section, with the current one opened to show its subsections. Listing every
  // subsection of every section would put 50 lines back in the sidebar.
  const contents = (
    <List dense disablePadding>
      <ListItemButton selected={!current && !searching} onClick={() => go()} sx={{ borderRadius: 1 }}>
        <ListItemText primary="Overview" primaryTypographyProps={{ fontWeight: !current ? 600 : 400 }} />
      </ListItemButton>
      {doc.sections.map(section => {
        const open = current?.slug === section.slug;
        return (
          <React.Fragment key={section.slug}>
            <ListItemButton selected={open && !subSlug && !searching} onClick={() => go(section.slug)} sx={{ borderRadius: 1 }}>
              <ListItemText
                primary={<InlineMarkdown text={section.title} />}
                primaryTypographyProps={{ fontWeight: open ? 600 : 400, sx: { overflowWrap: 'anywhere' } }}
              />
            </ListItemButton>
            {open && section.subsections.map(sub => (
              <ListItemButton
                key={sub.slug}
                selected={subSlug === sub.slug && !searching}
                onClick={() => go(section.slug, sub.slug)}
                sx={{ pl: 3.5, py: 0.25, borderRadius: 1 }}
              >
                <ListItemText
                  primary={<InlineMarkdown text={sub.title} />}
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', sx: { overflowWrap: 'anywhere' } }}
                />
              </ListItemButton>
            ))}
          </React.Fragment>
        );
      })}
    </List>
  );

  const prev = index > 0 ? doc.sections[index - 1] : undefined;
  const next = current ? doc.sections[index + 1] : doc.sections[0];
  const plain = (title: string) => title.replace(/[`*]/g, '');

  let body: React.ReactNode;
  if (searching) {
    body = results.length === 0 ? (
      // Say what was searched for, so "nothing" is not mistaken for a broken search
      <Typography variant="body2" color="text.secondary">
        Nothing in {noun} mentions all of: <strong>{query.trim()}</strong>. Fewer words find more.
      </Typography>
    ) : (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {results.length} {results.length === 1 ? 'place mentions' : 'places mention'} <strong>{query.trim()}</strong>
        </Typography>
        <List disablePadding>
          {results.map(r => (
            <ListItemButton
              key={`${r.sectionSlug}/${r.subsectionSlug || ''}`}
              onClick={() => go(r.sectionSlug || undefined, r.subsectionSlug)}
              sx={{ borderRadius: 1, alignItems: 'flex-start', px: 1 }}
            >
              <ListItemText
                primary={highlight(r.title, query)}
                secondary={highlight(r.snippet, query)}
                primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.6, overflowWrap: 'anywhere' } }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  } else if (current) {
    body = <Markdown source={current.body} anchorFor={anchorFor} onAnchorClick={onAnchorClick} />;
  } else {
    // Overview: the intro, then the contents as a page of its own — the sidebar is not
    // visible on a phone, and this is where someone arriving at the bare address starts.
    const intro = introOmit.reduce(
      (text, pattern) => text.replace(pattern, ''),
      // The document's own title is already the page header
      doc.intro.replace(/^# .*\n+/, '')
    );
    body = (
      <>
        <Markdown source={intro} />
        <Typography variant="h6" component="h2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Contents</Typography>
        <List dense disablePadding>
          {doc.sections.map(section => (
            <ListItemButton key={section.slug} onClick={() => go(section.slug)} sx={{ borderRadius: 1 }}>
              <ListItemText
                primary={<InlineMarkdown text={section.title} />}
                secondary={section.subsections.length ? section.subsections.map(s => plain(s.title)).join(' · ') : undefined}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
          ))}
        </List>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      {!isMobile && (
        <Box
          component="nav"
          aria-label="Contents"
          sx={{
            width: 270,
            flexShrink: 0,
            position: 'sticky',
            top: 16,
            // Scrolls on its own when the open section's subsections make it taller than the
            // window, rather than pushing the bottom entries out of reach
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
          }}
        >
          <Box sx={{ mb: 1.5 }}>{searchBox}</Box>
          {contents}
        </Box>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {searchBox}
            <TextField
              select
              size="small"
              label="Contents"
              value={current?.slug || ''}
              onChange={e => go(e.target.value || undefined)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Overview</MenuItem>
              {doc.sections.map(s => (
                <MenuItem key={s.slug} value={s.slug}>{plain(s.title)}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Card>
          <CardContent sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
            {body}

            {!searching && (prev || next || current) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  mt: 4,
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  {current && (
                    <Button startIcon={<PrevIcon />} onClick={() => go(prev?.slug)} sx={{ textTransform: 'none' }}>
                      {prev ? plain(prev.title) : 'Overview'}
                    </Button>
                  )}
                </Box>
                <Box>
                  {next && (
                    <Button endIcon={<NextIcon />} onClick={() => go(next.slug)} sx={{ textTransform: 'none' }}>
                      {plain(next.title)}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Link to this section copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
